import {useQueryClient} from '@tanstack/react-query';
import {Button, Card, Popconfirm, Spin, Tag} from 'antd';
import {format} from 'date-fns';
import {jwtDecode} from 'jwt-decode';
import toast from 'react-hot-toast';
import {useSelector} from 'react-redux';
import {Link} from 'react-router-dom';

import {RQGetOrderByUserId, RqUpdateOrder} from '../../../reactQuery/apis';
import {selectAuth} from '../../../redux/selectors';

const OrderHistory = () => {
  const auth = useSelector(selectAuth);
  const queryClient = useQueryClient();

  const token = auth?.tokenData;
  const decodedToken = token ? jwtDecode(token) : null;
  const id = decodedToken?.userId;

  const {mutate: updateOrder, isLoading: isUpdateLoading} = RqUpdateOrder({
    onSuccess: () => {
      toast.success('Order cancelled successfully');
      queryClient.invalidateQueries(['rq_get_order_by_user_id']);
    },
    onError: () => {
      toast.error('Failed to cancel order');
    },
  });

  const {data, isLoading} = RQGetOrderByUserId(id, token);

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center h-40'}>
        <Spin />
      </div>
    );
  }

  const getStatusColor = status => {
    switch (status) {
      case 'CANCELLED': return 'red';
      case 'PENDING':   return 'orange';
      case 'CONFIRMED': return 'green';
      case 'SHIPPED':   return 'blue';
      case 'DELIVERED': return 'purple';
      default:          return 'default';
    }
  };

  const sortedOrders = (data || [])
    .flatMap(order =>
      order.products.map(product => ({...product, order})),
    )
    .sort((a, b) => new Date(b.order.createdOn) - new Date(a.order.createdOn));

  const handleCancelOrder = orderId => {
    updateOrder({id: orderId, token, formData: {status: 'CANCELLED'}});
  };

  return (
    <div className={'w-full max-w-5xl mx-auto flex flex-col gap-3 px-2'}>
      {sortedOrders.length === 0 ? (
        <div className={'text-center p-6'}>
          <h1 className={'text-lg font-semibold mb-4'}>
            You haven t placed any orders yet..!
          </h1>
          <Link to={'/product'}>
            <Button className={'!bg-primary-800 !text-white'}>
              Continue Shopping
            </Button>
          </Link>
        </div>
      ) : (
        sortedOrders.map(({order, ...product}) => (
          <Card
            key={`${order.id}-${product.productId}`}
            className={'shadow-sm hover:shadow-md transition'}
          >
            <div className={'flex flex-row gap-4'}>

              {/* Image */}
              <Link
                to={`/product/${product.productInfo?.id}`}
                state={{product: product.productInfo}}
                className={'w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 self-start'}
              >
                <img
                  alt={'product'}
                  src={product.productInfo?.imageUrl?.[0] || ''}
                  className={'w-full h-full object-cover rounded-md'}
                />
              </Link>

              {/* Right side: details + cancel */}
              <div className={'flex flex-col flex-1 gap-2'}>

                {/* Details */}
                <div className={'flex flex-wrap gap-x-6 gap-y-2 text-sm'}>
                  <div className={'flex flex-col min-w-[120px]'}>
                    <span className={'font-medium text-primary-800'}>Product</span>
                    <span className={'break-words'}>{product.productName}</span>
                  </div>

                  <div className={'flex flex-col min-w-[100px]'}>
                    <span className={'font-medium text-primary-800'}>Date</span>
                    <span>{format(new Date(order.createdOn), 'dd MMM yyyy')}</span>
                  </div>

                  <div className={'flex flex-col min-w-[90px]'}>
                    <span className={'font-medium text-primary-800'}>Status</span>
                    <Tag color={getStatusColor(order.status)} className={'w-fit'}>
                      {order.status}
                    </Tag>
                  </div>

                  <div className={'flex flex-col min-w-[40px]'}>
                    <span className={'font-medium text-primary-800'}>Qty</span>
                    <span>{product.quantity}</span>
                  </div>
                </div>

                {/* Cancel button */}
                <div className={'flex justify-end mt-auto'}>
                  <Popconfirm
                    title={'Cancel Order'}
                    description={'Are you sure you want to cancel this order?'}
                    okText={'Yes'}
                    cancelText={'No'}
                    placement={'topRight'}
                    onConfirm={() => handleCancelOrder(order.id)}
                  >
                    <Button
                      danger
                      size={'small'}
                      loading={isUpdateLoading}
                      disabled={
                        order.status === 'SHIPPED' ||
                        order.status === 'DELIVERED' ||
                        order.status === 'CANCELLED'
                      }
                    >
                      Cancel
                    </Button>
                  </Popconfirm>
                </div>

              </div>
            </div>
          </Card>
        ))
      )}
    </div>
  );
};

export default OrderHistory;
